import React from 'react';
import type { ReactNode } from 'react';
import { Card, CardContent, Link, List, ListItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '~/tools/makeStyles';
import WorktangoWordmark from '../assets/worktango_wordmark.svg?react';
import { PRIVACY_NOTICE_URL, PRIVACY_POLICY_URL, TERMS_OF_USE_URL, WORKTANGO_URL } from '../helpers/link_helper';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    overflow: 'auto',
    backgroundColor: theme.palette.background.app,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
  },
  card: {
    width: 480,
    backgroundColor: theme.palette.common.white,
    marginTop: 12,
    [theme.breakpoints.down(500)]: {
      width: 'auto',
      maxWidth: 480,
    },
  },
  content: {
    padding: 24,
    '&:last-child': {
      paddingBottom: 36,
    },
  },
  banner: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 24,
    marginBottom: 32,
  },
  bannerLogo: {
    width: 'auto',
    height: 47,
  },
  poweredBy: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 48,
  },
  poweredText: {
    color: '#4f4f4f',
    fontSize: 14,
    marginRight: 8,
  },
  poweredLogo: {
    width: 'auto',
    height: 21,
  },
  footer: {
    color: theme.palette.darkaccentLight,
    textAlign: 'center',
    marginTop: 55,
  },
  footerLinks: {
    display: 'flex',
    flexDirection: 'row',
    padding: 0,
    justifyContent: 'center',
    '& li': {
      padding: '0 16px',
      width: 'unset',
      justifyContent: 'center',
      color: theme.palette.common.white,
      fontSize: 12,
      fontWeight: 500,
    },
  },
  copyright: {
    margin: '24px 16px 16px',
    color: '#f2f2f2',
    fontWeight: 400,
    fontSize: 12,
  },
}));

type BrandedCardLayoutProps = {
  children: ReactNode;
};

export const BrandedCardLayout = ({ children }: BrandedCardLayoutProps) => {
  const styles = useStyles();
  const { t } = useTranslation();

  return (
    <div className={styles.root}>
      <section className={styles.wrapper}>
        <Card className={styles.card}>
          <div className={styles.banner} role="banner">
            <WorktangoWordmark className={styles.bannerLogo} role="img" aria-label="WorkTango" />
          </div>

          <CardContent className={styles.content} role="main">
            {children}

            <div className={styles.poweredBy}>
              <Typography className={styles.poweredText}>{t('survey.kiosk.poweredby')}</Typography>
              <Link href={WORKTANGO_URL} underline="hover">
                <WorktangoWordmark className={styles.poweredLogo} role="img" aria-label="WorkTango" />
              </Link>
            </div>
          </CardContent>
        </Card>

        <Typography className={styles.footer} component="footer" role="contentinfo">
          <List className={styles.footerLinks}>
            <ListItem>
              <Link href={TERMS_OF_USE_URL} color="inherit" underline="hover">
                {t('user.terms')}
              </Link>
            </ListItem>
            <ListItem>
              <Link href={PRIVACY_POLICY_URL} color="inherit" underline="hover">
                {t('user.policy')}
              </Link>
            </ListItem>
            <ListItem>
              <Link href={PRIVACY_NOTICE_URL} color="inherit" underline="hover">
                {t('user.notice')}
              </Link>
            </ListItem>
          </List>
          <p className={styles.copyright}>{t('kiosk.footer', { year: new Date().getFullYear() })}</p>
        </Typography>
      </section>
    </div>
  );
};
